import { StaggerGroup } from '@/shared/ui/animations';

import { StackedHeader, SplitHeader, DetailHeader, BackHeader } from './headers';
import type { SectionLayoutProps } from './SectionLayout.types';

const HEADER = {
  stacked: StackedHeader,
  split: SplitHeader,
  detail: DetailHeader,
  back: BackHeader,
} as const;

/**
 * Stagger slots each header variant animates, so the content below can cascade
 * after the header. Keep in sync with each header's `StaggerItem` count: the
 * title always animates; `stacked` adds a slot for the subtitle, `detail` and `back` for
 * their actions. `split` animates only the title (its actions are static).
 */
const headerStaggerSlots = ({
  variant,
  subtitle,
  actions,
}: Pick<SectionLayoutProps, 'variant' | 'subtitle' | 'actions'>): number => {
  switch (variant) {
    case 'stacked':
      return subtitle ? 2 : 1;
    case 'detail':
    case 'back':
      return actions ? 2 : 1;
    default:
      return 1;
  }
};

export const SectionLayout = ({
  variant = 'stacked',
  children,
  ...headerProps
}: SectionLayoutProps) => {
  const Header = HEADER[variant];

  return (
    <div className="flex min-h-full w-full flex-col gap-2">
      <Header {...headerProps} />
      <StaggerGroup
        startIndex={headerStaggerSlots({
          variant,
          subtitle: headerProps.subtitle,
          actions: headerProps.actions,
        })}
      >
        <div className="flex flex-1 flex-col pb-4">{children}</div>
      </StaggerGroup>
    </div>
  );
};

SectionLayout.displayName = 'SectionLayout';
